import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '../api/errorUtils';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

// Staging deploy verification — 2026-06-09 (comment only, no behavior change)
// Redeploy trigger — 2026-09-05: restart container to free memory (comment only, no behavior change)
// Product API endpoints
const ENDPOINTS = {
  createProduct: 'techsavanna_pos.api.product_api.create_product',
  getProducts: 'techsavanna_pos.api.product_api.get_products',
  getProductDetails: 'techsavanna_pos.api.product_api.get_product_details',
  updateProduct: 'techsavanna_pos.api.product_api.update_product',
  deleteProduct: 'techsavanna_pos.api.product_api.delete_product',
  deleteProductPermanent: 'techsavanna_pos.api.product_api.delete_product_permanent',
  enableProduct: 'techsavanna_pos.api.product_api.enable_product',
  addBarcode: 'techsavanna_pos.api.product_api.add_barcode',
  removeBarcode: 'techsavanna_pos.api.product_api.remove_barcode',
  getProductPrice: 'techsavanna_pos.api.product_api.get_product_price',
  setProductPrice: 'techsavanna_pos.api.product_api.set_product_price',
  getStockQuantity: 'techsavanna_pos.api.product_api.get_stock_quantity',
  bulkCreateProducts: 'techsavanna_pos.api.product_api.bulk_create_products',
  getItemGroups: 'techsavanna_pos.api.product_api.get_item_groups',
  getBrands: 'techsavanna_pos.api.product_api.get_brands',
  getUOMs: 'techsavanna_pos.api.product_api.get_uoms',
  // Batch price lookup
  getItemPrices: 'techsavanna_pos.api.product_api.get_item_prices',
  // New endpoints
  bulkUpdatePrices: 'techsavanna_pos.api.product_api.bulk_update_prices',
  createProductVariant: 'techsavanna_pos.api.product_api.create_product_variant',
  getProductVariants: 'techsavanna_pos.api.product_api.get_product_variants',
  bulkImportProducts: 'techsavanna_pos.api.product_api.bulk_import_products',
  startBulkProductImport: 'techsavanna_pos.api.bulk_import.start_bulk_product_import',
  bulkProductImportStatus: 'techsavanna_pos.api.bulk_import.get_bulk_product_import_status',
  bulkImportOpeningStock: 'techsavanna_pos.api.product_api.bulk_import_opening_stock',
  createPriceList: 'techsavanna_pos.api.product_api.create_price_list',
  getPriceLists: 'techsavanna_pos.api.product_api.get_price_lists',
  updatePriceList: 'techsavanna_pos.api.product_api.update_price_list',
  deletePriceList: 'techsavanna_pos.api.product_api.delete_price_list',
  enablePriceList: 'techsavanna_pos.api.product_api.enable_price_list',
  disablePriceList: 'techsavanna_pos.api.product_api.disable_price_list',
  togglePriceList: 'techsavanna_pos.api.product_api.toggle_price_list',
  bulkDisablePriceLists: 'techsavanna_pos.api.product_api.bulk_disable_price_lists',
  createUOM: 'techsavanna_pos.api.product_api.create_uom',
  updateUOM: 'techsavanna_pos.api.product_api.update_uom',
  deleteUOM: 'techsavanna_pos.api.product_api.delete_uom',
  createItemGroup: 'techsavanna_pos.api.product_api.create_item_group',
  updateItemGroup: 'techsavanna_pos.api.product_api.update_item_group',
  deleteItemGroup: 'techsavanna_pos.api.product_api.delete_item_group',
  createBrand: 'techsavanna_pos.api.product_api.create_brand',
  updateBrand: 'techsavanna_pos.api.product_api.update_brand',
  deleteBrand: 'techsavanna_pos.api.product_api.delete_brand',
  setProductWarranty: 'techsavanna_pos.api.product_api.set_product_warranty',
  getProductWarranty: 'techsavanna_pos.api.product_api.get_product_warranty',
};

// Helper function to extract data from nested message response
// const extractResponseData = (response) => {
//   if (response.data?.message && typeof response.data.message === 'object') {
//     return response.data.message;
//   }
//   return response.data?.message || response.data;
// };
// Update extractResponseData function in productSlice.js:
const extractResponseData = (response) => {
  const data = response.data;
  
  // If response has message field, return it
  if (data?.message) {
    // If message is a string that looks like JSON, try to parse it
    if (typeof data.message === 'string') {
      try {
        const parsed = JSON.parse(data.message);
        return parsed;
      } catch (e) {
        // Not JSON, return as-is
        return data.message;
      }
    }
    // If message is an object, return it
    return data.message;
  }
  
  // Return data directly if no message field
  return data;
};

// Helper function to extract success message
const extractSuccessMessage = (response) => {
  if (response.data?.message && typeof response.data.message === 'string') {
    return response.data.message;
  }
  if (response.data?.message && typeof response.data.message === 'object') {
    if (response.data.message.message && typeof response.data.message.message === 'string') {
      return response.data.message.message;
    }
  }
  return null;
};

// Helper function to extract error message

// Helper function to determine notification severity based on HTTP status code
const getErrorSeverity = (error) => {
  // 409 Conflict should be shown as warning instead of error
  if (error.response?.status === 409) {
    return 'warning';
  }
  return 'error';
};

// Async thunks
// Body for get_products, shared by the paged list and the full export.
const buildProductsRequest = (params, state) => {
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      // Always prefer an explicit warehouse arg, then the active login till.
      // Search and browse both must send the till so backend exclusivity
      // (kitchen / shisha) is applied on every get_products call.
      const activeWh = state?.warehouse?.activeWarehouse;
      const resolvedWarehouse =
        params.warehouse ||
        activeWh?.name ||
        activeWh?.warehouse_name ||
        '';

      const requestBody = {
        company: params.company || userCompany,
        warehouse: resolvedWarehouse,
        item_group: params.item_group && params.item_group !== '' ? params.item_group : undefined,
        brand: params.brand && params.brand !== '' ? params.brand : null,
        is_stock_item: params.is_stock_item !== undefined ? params.is_stock_item : undefined,
        is_sales_item: params.is_sales_item !== undefined ? params.is_sales_item : undefined,
        // Pass through is_purchase_item when callers (e.g. purchase views) request it.
        // Left undefined for all other callers so axios omits it (no behavior change).
        is_purchase_item: params.is_purchase_item,
        disabled: params.disabled !== undefined ? params.disabled : true,
        // Only sent when the 'Disabled' filter option is chosen — backend then
        // returns disabled items exclusively.
        disabled_only: params.disabled_only || undefined,
        // When set, the backend hides Kitchen Foods module items (kitchen foods
        // + factors of production) so they never appear in the Products module.
        exclude_kitchen_items: params.exclude_kitchen_items || undefined,
        include_bom_availability: params.include_bom_availability || undefined,
        search_term: params.search_term || '',
        page: params.page || 1,
        page_size: params.page_size || params.limit || 20,
        // POS A→Z ordering (and any other caller-requested sort).
        order_by: params.order_by || undefined,
        // price_list: params.price_list,
      };
      return requestBody;
};

// Largest page get_products will return in one request.
const PRODUCTS_MAX_PAGE_SIZE = 500;
// Page sizes a product list may hold (10/20 for the other lists sharing this state).
const PRODUCT_PAGE_SIZES = [10, 20, 25, 50, 100, 250, 500];

// Every product matching the given filters, across all pages — for print /
// export. Does not touch the list held in the store.
export const getAllProductsForExport = createAsyncThunk(
  'product/getAllProductsForExport',
  async (params = {}, { dispatch, rejectWithValue, getState }) => {
    try {
      const all = [];
      for (let page = 1; page <= 1000; page += 1) {
        const body = buildProductsRequest({ ...params, page, page_size: PRODUCTS_MAX_PAGE_SIZE }, getState());
        const response = await axiosInstance.post(ENDPOINTS.getProducts, body);
        const data = extractResponseData(response);
        const message = data?.message || data;
        const batch = Array.isArray(message?.products) ? message.products : (Array.isArray(message) ? message : []);
        all.push(...batch);
        const total = Number(message?.pagination?.total);
        if (batch.length === 0 || batch.length < PRODUCTS_MAX_PAGE_SIZE || (total && all.length >= total)) break;
      }
      return all;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch products for export',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getProducts = createAsyncThunk(
  'product/getProducts',
  async (params = {}, { dispatch, rejectWithValue, getState }) => {
    try {
      const requestBody = buildProductsRequest(params, getState());

      // disabled: true = returns all products (enabled + disabled)
      // disabled: false = returns only enabled products

      const response = await axiosInstance.post(ENDPOINTS.getProducts, requestBody);
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch products',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getProductDetails = createAsyncThunk(
  'product/getProductDetails',
  async ({ itemCode, company }, { dispatch, rejectWithValue }) => {
    try {
      const params = { item_code: itemCode };
      if (company) params.company = company;
      
      const response = await axiosInstance.get(ENDPOINTS.getProductDetails, { params });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch product details',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (productData, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      const requestData = {
        ...productData,
        ...(productData.company ? {} : (userCompany ? { company: userCompany } : {})),
      };

      const hasImageFile =
        typeof File !== 'undefined' && requestData?.image instanceof File;

      let response;
      if (hasImageFile) {
        const formData = new FormData();
        Object.entries(requestData).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          formData.append(key, value);
        });
        // Do NOT set Content-Type manually: a hardcoded "multipart/form-data"
        // has no boundary, so the server can't parse the fields/file. Letting
        // axios serialize the FormData makes it add the correct boundary
        // (same pattern as startBulkProductImport).
        response = await axiosInstance.post(ENDPOINTS.createProduct, formData);
      } else {
        // Strip empty / null / undefined fields — Frappe's typing validator
        // rejects "" for Optional[float] params (e.g. buying_price=""), and
        // create_product's defaults cover omitted fields (same fix as
        // updateProduct below).
        const cleanPayload = Object.fromEntries(
          Object.entries(requestData).filter(
            ([, v]) => v !== undefined && v !== null && v !== '',
          ),
        );
        response = await axiosInstance.post(ENDPOINTS.createProduct, cleanPayload);
      }
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Product created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create product',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'product/updateProduct',
  async (productData, { dispatch, rejectWithValue }) => {
    try {
      const normalizedPayload = productData?.productData
        ? {
            item_code: productData.itemCode,
            ...productData.productData,
          }
        : productData;

      const hasImageFile =
        typeof File !== 'undefined' && normalizedPayload?.image instanceof File;

      let response;
      if (hasImageFile) {
        const formData = new FormData();
        Object.entries(normalizedPayload).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return;
          formData.append(key, value);
        });
        // Let axios set multipart/form-data with the correct boundary — a
        // hardcoded Content-Type omits the boundary and the server then can't
        // parse the uploaded image/fields.
        response = await axiosInstance.post(ENDPOINTS.updateProduct, formData);
      } else {
        // Strip empty / null / undefined fields — Frappe's typing validator
        // rejects "" for Optional[float]/Optional[int]/Optional[bool] params
        // (e.g. buying_price=""), and the backend's update_product treats
        // omitted fields as "no change" anyway.
        const cleanPayload = Object.fromEntries(
          Object.entries(normalizedPayload || {}).filter(
            ([k, v]) => v !== undefined && v !== null && (v !== '' || k === 'image'),
          ),
        );
        response = await axiosInstance.post(ENDPOINTS.updateProduct, cleanPayload);
      }
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Product updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update product',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'product/deleteProduct',
  async (itemCode, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.deleteProduct, {
        item_code: itemCode,
      });
      const successMessage = extractSuccessMessage(response) || 'Product disabled successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { itemCode };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to disable product',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProductPermanent = createAsyncThunk(
  'product/deleteProductPermanent',
  async (itemCode, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.deleteProductPermanent, {
        item_code: itemCode,
      });
      const successMessage = extractSuccessMessage(response) || 'Product deleted permanently';

      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));

      return { itemCode };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to delete product',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const enableProduct = createAsyncThunk(
  'product/enableProduct',
  async (itemCode, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.enableProduct, {
        item_code: itemCode,
      });
      const successMessage = extractSuccessMessage(response) || 'Product enabled successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { itemCode };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to enable product',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Disable several products in one action. Each item is disabled via the same
// delete_product endpoint used for the single-row disable; we summarise the
// outcome in one notification instead of one toast per product.
export const bulkDisableProducts = createAsyncThunk(
  'product/bulkDisableProducts',
  async (itemCodes, { dispatch, rejectWithValue }) => {
    const codes = (Array.isArray(itemCodes) ? itemCodes : [itemCodes]).filter(Boolean);

    if (codes.length === 0) {
      return { itemCodes: [], succeeded: 0, failed: 0 };
    }

    const results = await Promise.allSettled(
      codes.map((item_code) =>
        axiosInstance.post(ENDPOINTS.deleteProduct, { item_code })
      )
    );

    const failedResults = results.filter((r) => r.status === 'rejected');
    const succeeded = codes.length - failedResults.length;

    if (succeeded > 0) {
      dispatch(showNotification({
        message: `${succeeded} product${succeeded === 1 ? '' : 's'} disabled successfully${failedResults.length ? `; ${failedResults.length} could not be disabled` : ''}.`,
        severity: failedResults.length ? 'warning' : 'success',
        title: failedResults.length ? 'Partially Completed' : 'Success',
      }));
    }

    if (failedResults.length > 0 && succeeded === 0) {
      const message = extractErrorMessage(failedResults[0].reason);
      dispatch(showNotification({
        message,
        severity: getErrorSeverity(failedResults[0].reason),
        title: 'Failed to disable products',
      }));
      return rejectWithValue(message);
    }

    return { itemCodes: codes, succeeded, failed: failedResults.length };
  }
);

export const addBarcode = createAsyncThunk(
  'product/addBarcode',
  async ({ itemCode, barcode }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.addBarcode, {
        item_code: itemCode,
        barcode,
      });
      const successMessage = extractSuccessMessage(response) || 'Barcode added successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { itemCode, barcode };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to add barcode',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeBarcode = createAsyncThunk(
  'product/removeBarcode',
  async ({ itemCode, barcode }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.removeBarcode, {
        item_code: itemCode,
        barcode,
      });
      const successMessage = extractSuccessMessage(response) || 'Barcode removed successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { itemCode, barcode };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to remove barcode',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getProductPrice = createAsyncThunk(
  'product/getProductPrice',
  async ({ itemCode, priceList, company }, { dispatch, rejectWithValue }) => {
    try {
      const params = { item_code: itemCode };
      if (priceList) params.price_list = priceList;
      if (company) params.company = company;
      
      const response = await axiosInstance.get(ENDPOINTS.getProductPrice, { params });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch product price',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getItemPrices = createAsyncThunk(
  'product/getItemPrices',
  async ({ itemCodes, priceList, company }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.getItemPrices, {
        item_codes: itemCodes,
        price_list: priceList,
        ...(company ? { company } : {}),
      });
      const data = response.data?.message?.data ?? response.data?.message ?? {};
      return data; // { item_code: price }
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const setProductPrice = createAsyncThunk(
  'product/setProductPrice',
  async ({ itemCode, price, priceList, currency = 'KES' }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.setProductPrice, {
        item_code: itemCode,
        price,
        price_list: priceList,
        currency,
      });
      const successMessage = extractSuccessMessage(response) || 'Product price set successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { itemCode, price, priceList, currency };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to set product price',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getStockQuantity = createAsyncThunk(
  'product/getStockQuantity',
  async ({ itemCode, company, warehouse }, { dispatch, rejectWithValue }) => {
    try {
      const params = { item_code: itemCode };
      if (company) params.company = company;
      if (warehouse) params.warehouse = warehouse;
      
      const response = await axiosInstance.get(ENDPOINTS.getStockQuantity, { params });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch stock quantity',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkCreateProducts = createAsyncThunk(
  'product/bulkCreateProducts',
  async (products, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.bulkCreateProducts, {
        products,
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Bulk creation completed';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create products',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkImportProducts = createAsyncThunk(
  'product/bulkImportProducts',
  async ({ products_data, company }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.bulkImportProducts, {
        products_data,
        company,
      });
      const data = extractResponseData(response);
      
      let severity = 'success';
      let title = 'Success';
      let message = 'Bulk import completed successfully';

      if (data?.failure_count > 0 && data?.success_count > 0) {
        severity = 'warning';
        title = 'Partial Success';
        message = `Imported ${data.success_count} item(s). ${data.failure_count} item(s) failed.`;
      } else if (data?.success_count === 0 && data?.failure_count > 0) {
        severity = 'error';
        title = 'Import Failed';
        const failedDetails = data.failed?.slice(0, 3).map(f => `${f.item_code}: ${f.error}`).join('; ') || '';
        message = `All ${data.failure_count} item(s) failed to import.${failedDetails ? ` Details: ${failedDetails}${data.failed.length > 3 ? '...' : ''}` : ''}`;
      } else if (data?.success_count > 0) {
        message = `Successfully imported ${data.success_count} product(s).`;
      }

      dispatch(showNotification({
        message,
        severity,
        title,
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to import products',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getItemGroups = createAsyncThunk(
  'product/getItemGroups',
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getItemGroups, { params });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch item groups',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBrands = createAsyncThunk(
  'product/getBrands',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getBrands);
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch brands',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getUOMs = createAsyncThunk(
  'product/getUOMs',
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getUOMs, { params });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch units of measure',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk Update Prices
export const bulkUpdatePrices = createAsyncThunk(
  'product/bulkUpdatePrices',
  async (priceData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.bulkUpdatePrices, priceData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Prices updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update prices',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Product Variants
export const createProductVariant = createAsyncThunk(
  'product/createProductVariant',
  async (variantData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.createProductVariant, variantData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Product variant created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create product variant',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getProductVariants = createAsyncThunk(
  'product/getProductVariants',
  async ({ itemCode, company, page = 1, page_size = 10 }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getProductVariants, {
        params: { item_code: itemCode, company, page, page_size },
      });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch product variants',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Bulk Import Opening Stock
// Scalable bulk import: upload the raw file once; backend processes it in a
// background job. Returns an import_id to poll with getBulkProductImportStatus.
export const startBulkProductImport = createAsyncThunk(
  'product/startBulkProductImport',
  async ({ file, company }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (company) formData.append('company', company);
      // Let the browser/axios set the multipart boundary automatically.
      const response = await axiosInstance.post(ENDPOINTS.startBulkProductImport, formData);
      return extractResponseData(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const getBulkProductImportStatus = createAsyncThunk(
  'product/getBulkProductImportStatus',
  async ({ import_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.bulkProductImportStatus, { import_id });
      return extractResponseData(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const bulkImportOpeningStock = createAsyncThunk(
  'product/bulkImportOpeningStock',
  async (importData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.bulkImportOpeningStock, importData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Opening stock imported successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to import opening stock',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Price Lists
export const createPriceList = createAsyncThunk(
  'product/createPriceList',
  async (priceListData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.createPriceList, priceListData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Price list created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create price list',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getPriceLists = createAsyncThunk(
  'product/getPriceLists',
  async (params = {}, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = params.company || 
                         state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      // Build request parameters for new API interface
      const page = params.page || params.pagination?.page || 1;
      const pageSize = params.page_size || params.pagination?.page_size || params.limit || 20;
      
      // Extract filters from params
      const filters = params.filters || {
        selling: null,
        buying: null,
        enabled: 'all',
      };

      // New API request structure
      const requestParams = {
        // Filtering Parameters
        selling: filters.selling !== undefined && filters.selling !== null ? Boolean(filters.selling) : undefined,
        buying: filters.buying !== undefined && filters.buying !== null ? Boolean(filters.buying) : undefined,
        enabled: filters.enabled !== undefined ? filters.enabled : 'all',
        company: userCompany,
        
        // Search Parameters
        ...(params.search_term && { search_term: params.search_term }),
        
        // Pagination Parameters
        page: page,
        page_size: pageSize,
      };

      // Remove undefined values to match API expectations
      Object.keys(requestParams).forEach(key => {
        if (requestParams[key] === undefined) {
          delete requestParams[key];
        }
      });

      // Use POST for requests with the new API structure
      const response = await axiosInstance.post(ENDPOINTS.getPriceLists, requestParams);
      const data = extractResponseData(response);
      
      // Handle new response structure: { price_lists: [], pagination: { page, page_size, total, total_pages }, count, company, search_term, filters }
      const priceListsArray = data?.price_lists || (Array.isArray(data) ? data : []);
      const paginationData = data?.pagination;
      
      return {
        price_lists: priceListsArray,
        pagination: {
          page: paginationData?.page || page,
          page_size: paginationData?.page_size || pageSize,
          total: paginationData?.total || data?.count || 0,
          total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || data?.count || 0) / pageSize),
        },
        // Include additional response data for debugging/reference
        count: data?.count,
        company: data?.company,
        search_term: data?.search_term,
        filters: data?.filters,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch price lists',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updatePriceList = createAsyncThunk(
  'product/updatePriceList',
  async (priceListData, { dispatch, rejectWithValue }) => {
    try {
      // Validate required parameter
      if (!priceListData.name) {
        return rejectWithValue('Price list name (identifier) is required');
      }

      // API expects: { name (required), new_price_list_name?, currency?, selling?, buying?, enabled? }
      // Ensure boolean fields are properly typed as booleans
      const payload = {
        name: priceListData.name,
        ...(priceListData.new_price_list_name && { new_price_list_name: priceListData.new_price_list_name }),
        ...(priceListData.currency && { currency: priceListData.currency }),
        ...(priceListData.selling !== undefined && { selling: Boolean(priceListData.selling) }),
        ...(priceListData.buying !== undefined && { buying: Boolean(priceListData.buying) }),
        ...(priceListData.enabled !== undefined && { enabled: Boolean(priceListData.enabled) }),
      };

      const response = await axiosInstance.put(ENDPOINTS.updatePriceList, payload);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Price list updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update price list',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deletePriceList = createAsyncThunk(
  'product/deletePriceList',
  async ({ name, company }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(ENDPOINTS.deletePriceList, {
        params: { name, company },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Price list deleted successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, name };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to delete price list',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Price List Status Management
export const enablePriceList = createAsyncThunk(
  'product/enablePriceList',
  async (priceListName, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      const formData = new FormData();
      formData.append('price_list_name', priceListName);
      if (userCompany) formData.append('company', userCompany);

      const response = await axiosInstance.post(ENDPOINTS.enablePriceList, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Price list enabled successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, priceListName, enabled: true };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to enable price list',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const disablePriceList = createAsyncThunk(
  'product/disablePriceList',
  async (priceListName, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      const formData = new FormData();
      formData.append('price_list_name', priceListName);
      if (userCompany) formData.append('company', userCompany);

      const response = await axiosInstance.post(ENDPOINTS.disablePriceList, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Price list disabled successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, priceListName, enabled: false };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to disable price list',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const togglePriceList = createAsyncThunk(
  'product/togglePriceList',
  async ({ priceListName, enabled }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      const formData = new FormData();
      formData.append('price_list_name', priceListName);
      if (enabled !== undefined) {
        formData.append('enabled', String(enabled));
      }
      if (userCompany) formData.append('company', userCompany);

      const response = await axiosInstance.post(ENDPOINTS.togglePriceList, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 
        `Price list ${enabled ? 'enabled' : 'disabled'} successfully`;
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, priceListName, enabled };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to toggle price list status',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkDisablePriceLists = createAsyncThunk(
  'product/bulkDisablePriceLists',
  async (priceListNames, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userCompany = state?.auth?.user?.company ||
                         state?.auth?.user?.custom_company ||
                         state?.auth?.user?.company_name ||
                         state?.auth?.user?.company_data?.name ||
                         state?.auth?.user?.company_data?.company_name;

      const formData = new FormData();
      formData.append('price_list_names', JSON.stringify(priceListNames));
      if (userCompany) formData.append('company', userCompany);

      const response = await axiosInstance.post(ENDPOINTS.bulkDisablePriceLists, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 
        `${priceListNames.length} price lists disabled successfully`;
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Bulk Operation Success',
      }));
      
      return { ...data, priceListNames };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to disable price lists',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// UOM Management
export const createUOM = createAsyncThunk(
  'product/createUOM',
  async (uomData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.createUOM, uomData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Unit of measure created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create unit of measure',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUOM = createAsyncThunk(
  'product/updateUOM',
  async (uomData, { dispatch, rejectWithValue }) => {
    try {
      // Validate required parameter
      if (!uomData.name) {
        return rejectWithValue('UOM name (identifier) is required');
      }

      // API expects payload structure: { name (required), new_uom_name?, must_be_whole_number? }
      const payload = {
        name: uomData.name,
        ...(uomData.new_uom_name && { new_uom_name: uomData.new_uom_name }),
        ...(uomData.must_be_whole_number !== undefined && { 
          must_be_whole_number: Boolean(uomData.must_be_whole_number) 
        }),
      };

      const response = await axiosInstance.put(ENDPOINTS.updateUOM, payload);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Unit of measure updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update unit of measure',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteUOM = createAsyncThunk(
  'product/deleteUOM',
  async ({ uom_name, company }, { dispatch, rejectWithValue }) => {
    try {
      // API expects payload: { uom_name: "..." }
      const payload = { uom_name };
      
      const response = await axiosInstance.delete(ENDPOINTS.deleteUOM, {
        data: payload,
        params: company ? { company } : undefined,
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Unit of measure deleted successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, uom_name };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to delete unit of measure',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Item Groups Management
export const createItemGroup = createAsyncThunk(
  'product/createItemGroup',
  async (groupData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.createItemGroup, groupData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Item group created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create item group',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateItemGroup = createAsyncThunk(
  'product/updateItemGroup',
  async (groupData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(ENDPOINTS.updateItemGroup, groupData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Item group updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update item group',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteItemGroup = createAsyncThunk(
  'product/deleteItemGroup',
  async ({ name, company }, { dispatch, rejectWithValue }) => {
    try {
      if (!name) {
        return rejectWithValue('Category name is required');
      }

      // API expects JSON body with only name field: { "name": "Sub Assemblies" }
      const response = await axiosInstance.delete(ENDPOINTS.deleteItemGroup, {
        data: {
          name,
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Item group deleted successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, name };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to delete item group',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Brands Management
export const createBrand = createAsyncThunk(
  'product/createBrand',
  async (brandData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.createBrand, brandData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Brand created successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to create brand',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBrand = createAsyncThunk(
  'product/updateBrand',
  async (brandData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(ENDPOINTS.updateBrand, brandData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Brand updated successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to update brand',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteBrand = createAsyncThunk(
  'product/deleteBrand',
  async ({ brand_name, company }, { dispatch, rejectWithValue }) => {
    try {
      if (!brand_name) {
        return rejectWithValue('Brand name is required');
      }

      // API expects JSON body with brand_name
      const response = await axiosInstance.delete(ENDPOINTS.deleteBrand, {
        data: {
          brand_name,
          ...(company && { company }),
        },
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Brand deleted successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return { ...data, brand_name };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to delete brand',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

// Warranty Management
export const setProductWarranty = createAsyncThunk(
  'product/setProductWarranty',
  async (warrantyData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.setProductWarranty, warrantyData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || 'Warranty set successfully';
      
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Success',
      }));
      
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to set warranty',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const getProductWarranty = createAsyncThunk(
  'product/getProductWarranty',
  async ({ itemCode, company }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getProductWarranty, {
        params: { item_code: itemCode, company },
      });
      const data = extractResponseData(response);
      return data;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch warranty',
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  products: [],
  selectedProduct: null,
  itemGroups: [],
  itemGroupsPagination: null,
  brands: [],
  brandsPagination: null,
  uoms: [],
  uomsPagination: null,
  priceLists: [],
  // Full, unpaginated price list set for dropdowns. Kept separate from
  // priceLists so option fetches (page_size 1000) don't clobber the Price
  // Lists page's table rows, pagination, or loading flag.
  priceListOptions: [],
  variants: [],
  warranties: {},
  bulkImportResults: null,
  bulkUpdateResults: null,
  pagination: {
    page: 1,
        page_size: 10,
    total: 0,
    total_pages: 0,
  },
  filters: {
    search_term: '',
    item_group: '',
    brand: '',
    is_stock_item: undefined,
    is_sales_item: undefined,
    disabled: true, // true = all products (enabled + disabled), false = only enabled products
    warehouse: '',
    price_list: 'Standard Selling',
  },
  // Price list filters and pagination
  priceListFilters: {
    selling: null,  // null = all, true = selling only, false = non-selling only
    buying: null,   // null = all, true = buying only, false = non-buying only
    enabled: 'all', // 'all' = all, 'enabled' = enabled only, 'disabled' = disabled only
  },
  priceListPagination: {
    page: 1,
        page_size: 10,
    total: 0,
    total_pages: 0,
  },
  // Bulk operations state
  bulkOperations: {
    selectedPriceLists: [],
    isLoading: false,
  },
  // Individual operations state
  individualOperations: {
    loadingItems: [],
  },
  isLoading: false,
  isLoadingDetails: false,
  isLoadingReference: false,
  error: null,
};

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset to page 1 when filters change
      state.pagination.page = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    // setPageSize: (state, action) => {
    //   state.pagination.page_size = action.payload;
    //   // Reset to page 1 when page size changes
    //   state.pagination.page = 1;
    // },
    setPageSize: (state, action) => {
  // Validate that page_size is one of the allowed values
  const validPageSizes = PRODUCT_PAGE_SIZES;
  const newPageSize = parseInt(action.payload, 10);
  
  if (validPageSizes.includes(newPageSize)) {
    state.pagination.page_size = newPageSize;
  } else {
    // Default to 20 if invalid
    state.pagination.page_size = 20;
    console.warn(`Invalid page size: ${newPageSize}. Defaulting to 20. Valid options: ${validPageSizes.join(', ')}`);
  }
  
  // Reset to page 1 when page size changes
  state.pagination.page = 1;
},
    // Price list filters and pagination
    setPriceListFilters: (state, action) => {
      state.priceListFilters = { ...state.priceListFilters, ...action.payload };
      // Reset to page 1 when filters change
      state.priceListPagination.page = 1;
    },
    resetPriceListFilters: (state) => {
      state.priceListFilters = initialState.priceListFilters;
      state.priceListPagination.page = 1;
    },
    setPriceListPage: (state, action) => {
      state.priceListPagination.page = action.payload;
    },
    setPriceListPageSize: (state, action) => {
      state.priceListPagination.page_size = action.payload;
      state.priceListPagination.page = 1; // Reset to page 1 when page size changes
    },
    // Bulk operations reducers
    setSelectedPriceLists: (state, action) => {
      state.bulkOperations.selectedPriceLists = action.payload;
    },
    togglePriceListSelection: (state, action) => {
      const priceListName = action.payload;
      const selected = state.bulkOperations.selectedPriceLists;
      const index = selected.indexOf(priceListName);
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(priceListName);
      }
    },
    clearPriceListSelection: (state) => {
      state.bulkOperations.selectedPriceLists = [];
    },
    // Individual operations reducers
    setIndividualLoading: (state, action) => {
      const { priceListName, loading } = action.payload;
      if (loading) {
        if (!state.individualOperations.loadingItems.includes(priceListName)) {
          state.individualOperations.loadingItems.push(priceListName);
        }
      } else {
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get products
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // Keep the previous products array visible while the next fetch is in
        // flight — clearing it here briefly blanks the grid even on
        // background refetches (e.g. after edit/add), which read as
        // "products disappeared until I reload". The fulfilled handler
        // replaces state.products with the fresh list, so we never end up
        // with a stale view for more than the network round-trip.
      })
// In productSlice.js, update getProducts.fulfilled case:
.addCase(getProducts.fulfilled, (state, action) => {
  state.isLoading = false;
  
  // Debug logging
  console.log('getProducts.fulfilled - Full response:', action.payload);
  
  // Extract data from nested message structure
  let productsArray = [];
  let paginationData = null;
  
  if (action.payload?.message) {
    // Standard response structure with message wrapper
    const messageData = action.payload.message;
    
    if (Array.isArray(messageData?.products)) {
      productsArray = messageData.products;
    } else if (Array.isArray(messageData)) {
      // If message is directly an array
      productsArray = messageData;
    }
    
    paginationData = messageData?.pagination;
    
    console.log('getProducts.fulfilled - Extracted:', {
      productsCount: productsArray.length,
      paginationData,
      hasMessageWrapper: true
    });
    
  } else if (Array.isArray(action.payload?.products)) {
    // Alternative structure without message wrapper
    productsArray = action.payload.products;
    paginationData = action.payload?.pagination;
    
    console.log('getProducts.fulfilled - Extracted:', {
      productsCount: productsArray.length,
      paginationData,
      hasMessageWrapper: false
    });
  } else if (Array.isArray(action.payload)) {
    // If payload is directly an array
    productsArray = action.payload;
    
    console.log('getProducts.fulfilled - Extracted:', {
      productsCount: productsArray.length,
      paginationData: null,
      payloadIsArray: true
    });
  }
  
  // Ensure we always have an array
  if (!Array.isArray(productsArray)) {
    productsArray = [];
    console.warn('getProducts.fulfilled - No products array found');
  }
  
  state.products = productsArray;
  
  // Update pagination state
  if (paginationData) {
    // Only adopt the API's page_size when it is a real UI page size.
    // getProducts is a shared thunk — callers like the Dashboard's
    // product-existence check (page_size: 1) or reference-data loads
    // (page_size: 1000) must NOT corrupt the product list's pagination,
    // otherwise the list later re-fetches with that size and shows e.g.
    // a single row on mount. Keep the current page_size in those cases.
    const validPageSizes = PRODUCT_PAGE_SIZES;
    const respPageSize = Number(paginationData.page_size);
    const pageSize = validPageSizes.includes(respPageSize)
      ? respPageSize
      : state.pagination.page_size;
    const total = Number(paginationData.total) || 0;
    state.pagination = {
      page: Number(paginationData.page) || state.pagination.page,
      page_size: pageSize,
      total,
      total_pages: total && pageSize ? Math.ceil(total / pageSize) : 0,
    };
  } else {
    // Fallback: calculate from products count (not ideal)
    console.warn('getProducts.fulfilled - No pagination data from API');
    const productsCount = productsArray.length;
    state.pagination = {
      page: state.pagination.page,
      page_size: state.pagination.page_size,
      total: productsCount,
      total_pages: Math.ceil(productsCount / state.pagination.page_size) || 1,
    };
  }
  
  console.log('getProducts.fulfilled - Final state:', {
    productsCount: state.products.length,
    pagination: state.pagination,
    pageSize: state.pagination.page_size
  });
})
      .addCase(getProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get product details
      .addCase(getProductDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.error = null;
      })
      .addCase(getProductDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.selectedProduct = action.payload.product || null;
      })
      .addCase(getProductDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.error = action.payload;
      })
      // Create product
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.product) {
          state.products.unshift(action.payload.product);
          state.pagination.total += 1;
        }
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.product) {
          const index = state.products.findIndex(
            (p) => p.item_code === action.payload.product.item_code
          );
          if (index !== -1) {
            state.products[index] = { ...state.products[index], ...action.payload.product };
          }
          if (state.selectedProduct?.item_code === action.payload.product.item_code) {
            state.selectedProduct = { ...state.selectedProduct, ...action.payload.product };
          }
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete product
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(
          (p) => p.item_code === action.payload.itemCode
        );
        if (index !== -1) {
          state.products[index].disabled = 1;
        }
        if (state.selectedProduct?.item_code === action.payload.itemCode) {
          state.selectedProduct.disabled = 1;
        }
      })
      // Enable product
      .addCase(enableProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(
          (p) => p.item_code === action.payload.itemCode
        );
        if (index !== -1) {
          state.products[index].disabled = 0;
        }
        if (state.selectedProduct?.item_code === action.payload.itemCode) {
          state.selectedProduct.disabled = 0;
        }
      })
      // Add barcode
      .addCase(addBarcode.fulfilled, (state, action) => {
        const index = state.products.findIndex(
          (p) => p.item_code === action.payload.itemCode
        );
        if (index !== -1 && !state.products[index].barcodes) {
          state.products[index].barcodes = [];
        }
        if (index !== -1 && !state.products[index].barcodes.includes(action.payload.barcode)) {
          state.products[index].barcodes.push(action.payload.barcode);
        }
        if (state.selectedProduct?.item_code === action.payload.itemCode) {
          if (!state.selectedProduct.barcodes) {
            state.selectedProduct.barcodes = [];
          }
          if (!state.selectedProduct.barcodes.includes(action.payload.barcode)) {
            state.selectedProduct.barcodes.push(action.payload.barcode);
          }
        }
      })
      // Remove barcode
      .addCase(removeBarcode.fulfilled, (state, action) => {
        const index = state.products.findIndex(
          (p) => p.item_code === action.payload.itemCode
        );
        if (index !== -1 && state.products[index].barcodes) {
          state.products[index].barcodes = state.products[index].barcodes.filter(
            (b) => b !== action.payload.barcode
          );
        }
        if (state.selectedProduct?.item_code === action.payload.itemCode) {
          if (state.selectedProduct.barcodes) {
            state.selectedProduct.barcodes = state.selectedProduct.barcodes.filter(
              (b) => b !== action.payload.barcode
            );
          }
        }
      })
      // Get item groups
      .addCase(getItemGroups.pending, (state) => {
        state.isLoadingReference = true;
      })
      .addCase(getItemGroups.fulfilled, (state, action) => {
        state.isLoadingReference = false;
        state.itemGroups = action.payload.item_groups || [];
        state.itemGroupsPagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total: action.payload.total,
          total_pages: action.payload.total_pages,
        };
      })
      .addCase(getItemGroups.rejected, (state) => {
        state.isLoadingReference = false;
      })
      // Get brands
      .addCase(getBrands.pending, (state) => {
        state.isLoadingReference = true;
      })
      .addCase(getBrands.fulfilled, (state, action) => {
        state.isLoadingReference = false;
        state.brands = action.payload.brands || [];
        state.brandsPagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total: action.payload.total,
          total_pages: action.payload.total_pages,
        };
      })
      .addCase(getBrands.rejected, (state) => {
        state.isLoadingReference = false;
      })
      // Get UOMs
      .addCase(getUOMs.pending, (state) => {
        state.isLoadingReference = true;
      })
      .addCase(getUOMs.fulfilled, (state, action) => {
        state.isLoadingReference = false;
        state.uoms = action.payload.uoms || [];
        state.uomsPagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total: action.payload.total,
          total_pages: action.payload.total_pages,
        };
      })
      .addCase(getUOMs.rejected, (state) => {
        state.isLoadingReference = false;
      })
      // Bulk update prices
      .addCase(bulkUpdatePrices.fulfilled, (state, action) => {
        state.bulkUpdateResults = action.payload;
      })
      // Product variants
      .addCase(getProductVariants.fulfilled, (state, action) => {
        state.variants = action.payload.variants || [];
      })
      .addCase(createProductVariant.fulfilled, (state, action) => {
        if (action.payload.variant) {
          state.variants.push(action.payload.variant);
        }
      })
      // Bulk import opening stock
      .addCase(bulkImportOpeningStock.fulfilled, (state, action) => {
        state.bulkImportResults = action.payload;
      })
      // Price lists
      .addCase(getPriceLists.pending, (state, action) => {
        // Dropdown option fetches must not flip the table's loading overlay.
        if (action.meta.arg?.purpose === 'options') return;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPriceLists.fulfilled, (state, action) => {
        if (action.meta.arg?.purpose === 'options') {
          state.priceListOptions = action.payload.price_lists || [];
          return;
        }
        state.isLoading = false;
        state.priceLists = action.payload.price_lists || [];

        // Update pagination state from response
        if (action.payload.pagination) {
          const total = action.payload.pagination.total || action.payload.pagination.count || 0;
          const pageSize = action.payload.pagination.page_size || state.priceListPagination.page_size;
          
          state.priceListPagination = {
            page: action.payload.pagination.page || state.priceListPagination.page,
            page_size: pageSize,
            total: total,
            total_pages: action.payload.pagination.total_pages || 
              (total > 0 && pageSize > 0 ? Math.ceil(total / pageSize) : 0),
          };
        }
      })
      .addCase(getPriceLists.rejected, (state, action) => {
        if (action.meta.arg?.purpose === 'options') return;
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createPriceList.fulfilled, (state, action) => {
        if (action.payload.price_list) {
          state.priceLists.push(action.payload.price_list);
        }
      })
      .addCase(updatePriceList.fulfilled, (state, action) => {
        if (action.payload.price_list) {
          const index = state.priceLists.findIndex(
            (pl) => pl.name === action.payload.price_list.name
          );
          if (index !== -1) {
            state.priceLists[index] = action.payload.price_list;
          }
        }
      })
      .addCase(deletePriceList.fulfilled, (state, action) => {
        state.priceLists = state.priceLists.filter(
          (pl) => pl.name !== action.payload.name
        );
      })
      // Price list status management
      .addCase(enablePriceList.pending, (state, action) => {
        const priceListName = action.meta.arg;
        state.individualOperations.loadingItems.push(priceListName);
      })
      .addCase(enablePriceList.fulfilled, (state, action) => {
        const { priceListName } = action.payload;
        // Update the price list status optimistically
        const priceList = state.priceLists.find(pl => pl.name === priceListName || pl.price_list_name === priceListName);
        if (priceList) {
          priceList.enabled = 1;
        }
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(enablePriceList.rejected, (state, action) => {
        const priceListName = action.meta.arg;
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(disablePriceList.pending, (state, action) => {
        const priceListName = action.meta.arg;
        state.individualOperations.loadingItems.push(priceListName);
      })
      .addCase(disablePriceList.fulfilled, (state, action) => {
        const { priceListName } = action.payload;
        // Update the price list status optimistically
        const priceList = state.priceLists.find(pl => pl.name === priceListName || pl.price_list_name === priceListName);
        if (priceList) {
          priceList.enabled = 0;
        }
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(disablePriceList.rejected, (state, action) => {
        const priceListName = action.meta.arg;
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(togglePriceList.pending, (state, action) => {
        const { priceListName } = action.meta.arg;
        state.individualOperations.loadingItems.push(priceListName);
      })
      .addCase(togglePriceList.fulfilled, (state, action) => {
        const { priceListName, enabled } = action.payload;
        // Update the price list status optimistically
        const priceList = state.priceLists.find(pl => pl.name === priceListName || pl.price_list_name === priceListName);
        if (priceList) {
          priceList.enabled = enabled ? 1 : 0;
        }
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(togglePriceList.rejected, (state, action) => {
        const { priceListName } = action.meta.arg;
        // Remove from loading items
        state.individualOperations.loadingItems = state.individualOperations.loadingItems.filter(
          item => item !== priceListName
        );
      })
      .addCase(bulkDisablePriceLists.pending, (state) => {
        state.bulkOperations.isLoading = true;
      })
      .addCase(bulkDisablePriceLists.fulfilled, (state, action) => {
        const { priceListNames } = action.payload;
        // Update all affected price lists
        priceListNames.forEach(priceListName => {
          const priceList = state.priceLists.find(pl => pl.name === priceListName || pl.price_list_name === priceListName);
          if (priceList) {
            priceList.enabled = 0;
          }
        });
        state.bulkOperations.isLoading = false;
        state.bulkOperations.selectedPriceLists = [];
      })
      .addCase(bulkDisablePriceLists.rejected, (state) => {
        state.bulkOperations.isLoading = false;
      })
      // UOM management
      .addCase(createUOM.fulfilled, (state, action) => {
        if (action.payload.uom) {
          state.uoms.push(action.payload.uom);
        }
      })
      .addCase(updateUOM.fulfilled, (state, action) => {
        if (action.payload.uom) {
          const index = state.uoms.findIndex(
            (uom) => uom.name === action.payload.uom.name
          );
          if (index !== -1) {
            state.uoms[index] = action.payload.uom;
          }
        }
      })
      .addCase(deleteUOM.fulfilled, (state, action) => {
        const deletedUomName = action.payload.uom_name;
        state.uoms = state.uoms.filter((uom) => 
          uom.name !== deletedUomName && uom.uom_name !== deletedUomName
        );
      })
      // Item groups management
      .addCase(createItemGroup.fulfilled, (state, action) => {
        if (action.payload.item_group) {
          state.itemGroups.push(action.payload.item_group);
        }
      })
      .addCase(updateItemGroup.fulfilled, (state, action) => {
        if (action.payload.item_group) {
          const index = state.itemGroups.findIndex(
            (ig) => ig.name === action.payload.item_group.name
          );
          if (index !== -1) {
            state.itemGroups[index] = action.payload.item_group;
          }
        }
      })
      .addCase(deleteItemGroup.fulfilled, (state, action) => {
        state.itemGroups = state.itemGroups.filter(
          (ig) => ig.name !== action.payload.name
        );
      })
      // Brands management
      .addCase(createBrand.fulfilled, (state, action) => {
        if (action.payload.brand) {
          state.brands.push(action.payload.brand);
        }
      })
      .addCase(updateBrand.fulfilled, (state, action) => {
        if (action.payload.brand) {
          const index = state.brands.findIndex(
            (b) => b.name === action.payload.brand.name
          );
          if (index !== -1) {
            state.brands[index] = action.payload.brand;
          }
        }
      })
      .addCase(deleteBrand.fulfilled, (state, action) => {
        state.brands = state.brands.filter((b) => b.name !== action.payload.name);
      })
      // Warranty management
      .addCase(getProductWarranty.fulfilled, (state, action) => {
        if (action.payload.warranty && action.payload.item_code) {
          state.warranties[action.payload.item_code] = action.payload.warranty;
        }
      })
      .addCase(setProductWarranty.fulfilled, (state, action) => {
        if (action.payload.warranty && action.payload.item_code) {
          state.warranties[action.payload.item_code] = action.payload.warranty;
        }
      });
  },
});

export const { 
  clearSelectedProduct, 
  clearError, 
  setFilters, 
  resetFilters, 
  setPage, 
  setPageSize,
  setPriceListFilters,
  resetPriceListFilters,
  setPriceListPage,
  setPriceListPageSize,
  setSelectedPriceLists,
  togglePriceListSelection,
  clearPriceListSelection,
  setIndividualLoading,
} = productSlice.actions;
export default productSlice.reducer;

